import React, { ReactNode, Ref, useMemo } from 'react';

import { use$ } from '@legendapp/state/react';
import {
	Breakpoints,
	Disclaimers,
	Richtext,
	Text,
	TokenTextAppearance,
	TokenTextColor,
	createTagOverride,
	styled
} from '@volkswagen-onehub/components-core';
import ReactDOM from 'react-dom';
import { css } from 'styled-components';

import { landscapeMediaMixin } from '../../hooks/use-vertical-breakpoint';

import { getRichtextOverrides } from '../../utils/richtext';
import translate from '../../utils/translate';

import { disclaimerStore, emissionStore, rootStore } from '../../store';
import { testId } from '../../test-utils/constants';
import { AdditionalContent, DisclaimerCloseButton, VictorContent } from './disclaimer-components';
import Efficiency from './efficiency';

interface TextWithReferences {
	[k: string]: string;
}

interface Props {
	text: TextWithReferences | string;
	extraText?: string;
	closeAriaLabel: string;
	isFullIaa?: boolean;
	closeDisclaimer: () => void;
	children?: ReactNode;
	closeDisclaimerRef: Ref<HTMLButtonElement>;
}

const disclaimerRichTextOverridesTextStyle = createTagOverride(Text, {
	appearance: TokenTextAppearance.label200,
	color: TokenTextColor.secondary
});

const disclaimerRichTextOverrides = getRichtextOverrides({
	p: disclaimerRichTextOverridesTextStyle,
	span: disclaimerRichTextOverridesTextStyle
});

const getPlaceholder = (str: string[]) => `$${str.join('.')}$`;

export function Disclaimer({ text, closeAriaLabel, closeDisclaimer, closeDisclaimerRef }: Props) {
	const disclaimerId = use$(disclaimerStore.disclaimerId);
	const domNode = document.getElementById(disclaimerId);

	const [_, currentModelValue] = use$(rootStore.currentModel);
	const trimName = currentModelValue.vehicleConfiguration?.trimName || '';

	const isEmissionDataVisible = use$(emissionStore.isEmissionDataVisible);

	const memoizedText = useMemo(() => {
		const key = ['variants', trimName, 'teaser', 'disclaimer'];
		const globalKey = ['global', 'teaser', 'disclaimer'];

		let text = translate(key);

		if (text === getPlaceholder(key)) {
			text = translate(globalKey);
		}

		if (text === getPlaceholder(globalKey) || text === '') {
			return null;
		}

		return text;
	}, [trimName]);

	if (!domNode) return null;

	const isVictorContentVisible = memoizedText || isEmissionDataVisible;

	const content = (
		<Container id={testId('Disclaimer_Container')} data-testid={testId('Disclaimer_Container')}>
			<StyledDisclaimerWrapper>
				{isVictorContentVisible && (
					<VictorContent>
						{memoizedText && (
							<Disclaimers
								disclaimers={[
									{
										key: 'i',
										reference: 'i',
										text: (
											<Richtext
												markdown={memoizedText}
												dangerouslyParseRawHTML
												overrides={disclaimerRichTextOverrides}
											/>
										)
									}
								]}
							/>
						)}
						{isEmissionDataVisible && <Efficiency />}
					</VictorContent>
				)}
				<AdditionalContent text={text} />
			</StyledDisclaimerWrapper>
			<DisclaimerCloseButton
				closeAriaLabel={closeAriaLabel}
				closeDisclaimer={closeDisclaimer}
				closeDisclaimerRef={closeDisclaimerRef}
			/>
		</Container>
	);

	return ReactDOM.createPortal(content, domNode);
}

const Container = styled.div`
	position: absolute;
	width: 100%;
	background-color: rgba(0, 0, 0, 0.7);
	left: 0;
	bottom: 0;
	max-height: 200px;
	overflow-y: auto;
	-ms-overflow-style: none;
	scrollbar-width: none;
	z-index: 1;

	&::-webkit-scrollbar {
		display: none;
	}

	@media (min-width: ${Breakpoints.b560}px) {
		border-top-left-radius: ${({ theme }) => theme.size.static100};
		border-top-right-radius: ${({ theme }) => theme.size.static100};
		max-width: 80vw;
		transform: translateX(-50%);
		left: 50%;
	}
	@media (min-width: ${Breakpoints.b960}px) {
		max-height: 150px;
	}
	@media ${landscapeMediaMixin} {
		max-height: 100px;
	}
`;

// The width of padding for the disclaimer close button
const closeButtonWidth = '32px';
const StyledDisclaimerWrapper = styled.div`
	// Add padding bottom to everything but the last child

	& > *:not(:last-child) {
		padding-bottom: ${props => props.theme.size.static300};
	}

	${({ theme }) => {
		const paddingSide = theme.direction === 'ltr' ? 'right' : 'left';
		const oppositePaddingSide = theme.direction === 'ltr' ? 'left' : 'right';

		return css`
            padding-top: ${theme.size.static250};
            padding-${paddingSide}: calc(${theme.size.static250} + ${closeButtonWidth});
            padding-bottom: ${theme.size.static250};
            padding-${oppositePaddingSide}: ${theme.size.static250};
        `;
	}}
`;
